// v4 addendum "Make it in Suno" (docs/product/spec-v4-suno.md, ticket V4-M1): the Suno prompt pack for a video. The
// steps, each field in its own box with a full-width Copy button, the settings to match (read-only, each with a short
// "why"), "Write a new version" and an inline Edit, then the licensing note and the upload (the V4-A1 pipeline:
// POST /api/uploads -> PUT -> complete, the terms checkbox the first time) with its progress. Once the song is in: its
// length, and for a short song the loop-or-shorten choice.
//
// <SunoPack> is the Fold in the project's Music section (the music strip; the Song panel of a music video).
// <SunoSheet> is the same body opened by the next step "Upload your Suno song". Phone first: the fields stack, every
// copy button is full width and the upload is pinned at the bottom of the sheet.
import { useEffect, useRef, useState, type DragEvent } from "react";
import { api, ApiError } from "../api";
import { toggleAudio } from "../audio";
import { director } from "../director";
import { UPLOAD_TERMS_CONSENT } from "../legal/consent";
import { bump, toast, useStore } from "../store";
import type { PaidOutcome } from "../actions";
import { paidFailure } from "../produce";
import { PaidButton, usePrice } from "./PriceFirst"; // PRICE-FIRST
import {
  copyText,
  progressText,
  SONG_ACCEPT,
  songProblem,
  SUNO_LICENSE_NOTE,
  sunoPackCredits,
  SUNO_STEPS,
  SUNO_URL,
  sunoFields,
  sunoSettings,
  sunoStateLabel,
  time,
  uploadLabel,
  type SunoFieldKey,
  type SunoPackData,
  type SunoPatch,
  type SunoState,
} from "../suno";
import type { Project } from "../types";
import { Icon, Sheet } from "./bits";
import { Fold } from "./Fold";
import { Notice } from "./Notice";

type Props = { p: Project; onSuno?: (s: SunoState) => void };

/** The fold in the Music section. Its price (before a pack exists) and its notices sit on or above the row. */
export function SunoPack({ p, onSuno }: Props) {
  const s = p.suno ?? null;
  const label = sunoStateLabel(s);
  return (
    <Fold id={`music.suno.${p.id}`} title="Make it in Suno" summary={label.text} price={s?.pack ? null : (s?.credits ?? sunoPackCredits())} notices={s?.notices} defaultOpen={!!s && s.status === "waiting"} className="suno-fold">
      <SunoPackBody p={p} onSuno={onSuno} />
    </Fold>
  );
}

/** The next step's sheet: the same body, open, with the notices on top. */
export function SunoSheet({ p, onClose, onSuno }: Props & { onClose: () => void }) {
  const s = p.suno ?? null;
  return (
    <Sheet title="Make it in Suno" onClose={onClose} className="suno-sheet">
      <div className="sheet-body">
        {s?.notices.map((n, i) => <Notice key={`${n.code ?? n.tone}-${i}`} {...n} />)}
        <SunoPackBody p={p} onSuno={onSuno} />
      </div>
    </Sheet>
  );
}

async function copy(text: string, what: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast(`Copied the ${what.toLowerCase()}.`, "good");
  } catch {
    toast("Couldn't reach the clipboard. Press and hold the text to copy it.", "bad");
  }
}

export function SunoPackBody({ p, onSuno }: Props) {
  // what an action just returned, until the project reloads with it
  const [own, setOwn] = useState<SunoState | null>(null);
  useEffect(() => setOwn(null), [p.suno]);
  const s = own ?? p.suno ?? null;
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const took = (next: SunoState) => {
    setOwn(next);
    onSuno?.(next);
    bump("project");
  };

  // PRICE-FIRST: the pack's price from the api (falls back to what the pack says), on the button and sent with the tap
  const quoted = usePrice(p.id, "suno");
  const price = quoted !== undefined ? quoted : { step: "suno" as const, credits: s?.credits ?? sunoPackCredits(), clips: 0, needsYes: false };
  const write = async (expectCredits: number): Promise<PaidOutcome> => {
    setBusy(true);
    try {
      const r = await api.writeSunoPack(p.id, { ...(s?.pack ? { instrumental: s.pack.instrumental } : {}), expectCredits });
      took(r.suno);
      bump("credits");
      director.context(`The user tapped "${s?.pack ? "Write a new version" : "Write the Suno prompts"}" on "${p.title}" (${p.id}): a Suno pack (version ${r.suno.version}) is ready, ${r.credits} credits. They'll make the song in Suno and upload it. Don't generate music for this video.`);
      return { kind: "ok", job: null };
    } catch (e) {
      bump("credits");
      return paidFailure(e);
    } finally {
      setBusy(false);
    }
  };
  const ask = { title: "Write the Suno prompts?", lede: "Everything Suno needs for this video's song: the style, the words and the settings.", go: "Write them" };

  if (!s?.pack)
    return (
      <div className="suno">
        <p className="suno-lede">
          Make this video's music yourself in Suno. We write everything Suno needs, fitted to this video: the style, {p.format === "music-video" ? "the lyrics" : "what to leave out"} and the settings. You make the song and upload it here.
        </p>
        <PaidButton price={price} label="Write the Suno prompts" className="btn btn-primary suno-full" dataAction="suno.write" disabled={busy} ask={ask} run={write} />
        {s?.status === "received" && <SunoSong p={p} s={s} />}
        {s && <SunoUpload p={p} s={s} onSuno={took} />}
      </div>
    );

  const patch = async (body: SunoPatch, said: string) => {
    setBusy(true);
    try {
      took(await api.patchSunoPack(p.id, body));
      director.context(`The user ${said} on "${p.title}" (${p.id}).`);
      return true;
    } catch (e) {
      toast(`Couldn't save that. ${(e as Error).message}`, "bad");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const onFit = (fit: "loop" | "shorten") => void patch({ fit }, fit === "loop" ? "chose to loop the Suno song" : "chose to shorten the video to fit the Suno song");
  return (
    <div className="suno">
      {s.status === "received" && <SunoSong p={p} s={s} onFit={onFit} busy={busy} />}
      <ol className="suno-steps">
        {SUNO_STEPS.map((t, i) => (
          <li key={t}>
            {i === 0 ? (
              <span>
                Open{" "}
                <a href={SUNO_URL} target="_blank" rel="noopener noreferrer">
                  suno.com/create
                </a>{" "}
                and switch to Custom.
              </span>
            ) : (
              <span>{t}</span>
            )}
          </li>
        ))}
      </ol>

      {editing ? (
        <SunoEdit
          pack={s.pack}
          busy={busy}
          onCancel={() => setEditing(false)}
          onSave={async (body) => {
            if (await patch(body, "edited the Suno prompts")) setEditing(false);
          }}
        />
      ) : (
        <>
          <div className="suno-fields">
            {sunoFields(s.pack).map((f) => (
              <SunoField key={f.key} pack={s.pack!} k={f.key} label={f.label} text={f.text} hint={f.hint} limit={f.limit} multiline={f.multiline} />
            ))}
          </div>
          <h3 className="suno-h">Settings in Suno</h3>
          <dl className="suno-settings">
            {sunoSettings(s.pack, s.why).map((r) => (
              <div key={r.key} className="suno-setting">
                <dt>{r.label}</dt>
                <dd>
                  <strong>{r.value}</strong>
                  {r.why && <span>{r.why}</span>}
                </dd>
              </div>
            ))}
          </dl>
          {!!s.pack.notes.length && (
            <ul className="suno-notes">
              {s.pack.notes.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          )}
          <div className="suno-acts">
            <button type="button" className="btn btn-quiet" disabled={busy} onClick={() => setEditing(true)}>
              <Icon name="edit" size={16} /> Edit
            </button>
            <PaidButton price={price} label="Write a new version" icon="redo" className="btn btn-quiet" dataAction="suno.write" disabled={busy} ask={{ ...ask, title: "Write a new version?" }} run={write} />
          </div>
        </>
      )}

      <SunoUpload p={p} s={s} onSuno={took} />
    </div>
  );
}

function SunoField({ pack, k, label, text, hint, limit, multiline }: { pack: SunoPackData; k: SunoFieldKey; label: string; text: string; hint: string; limit?: number; multiline: boolean }) {
  return (
    <section className={`suno-field suno-field-${k}`} aria-label={label}>
      <div className="suno-field-head">
        <h3>{label}</h3>
        {limit && multiline && (
          <span className="suno-count">
            {text.length} / {limit}
          </span>
        )}
      </div>
      <pre className={`suno-text${multiline ? "" : " is-line"}`}>{text || "—"}</pre>
      <p className="suno-hint">{hint}</p>
      <button type="button" className="btn btn-quiet suno-full suno-copy" disabled={!copyText(pack, k)} onClick={() => void copy(copyText(pack, k), label)}>
        <Icon name="copy" size={16} /> Copy {label.toLowerCase()}
      </button>
    </section>
  );
}

function SunoEdit({ pack, busy, onCancel, onSave }: { pack: SunoPackData; busy: boolean; onCancel: () => void; onSave: (b: SunoPatch) => void }) {
  const [d, setD] = useState({ ...pack });
  const set = <K extends keyof SunoPackData>(k: K, v: SunoPackData[K]) => setD((x) => ({ ...x, [k]: v }));
  const changed: SunoPatch = {};
  for (const k of ["title", "style", "exclude", "lyrics", "instrumental", "weirdness", "styleInfluence", "maxMode"] as const) if (d[k] !== pack[k]) (changed as Record<string, unknown>)[k] = d[k];
  return (
    <form
      className="suno-edit"
      onSubmit={(e) => {
        e.preventDefault();
        if (Object.keys(changed).length) onSave(changed);
        else onCancel();
      }}
    >
      <label className="suno-check">
        <input type="checkbox" checked={d.instrumental} onChange={(e) => set("instrumental", e.target.checked)} /> Instrumental (no singing)
      </label>
      {!d.instrumental && (
        <label className="field">
          <span className="field-label">Lyrics</span>
          <textarea rows={10} maxLength={pack.limits.lyricsChars} value={d.lyrics} onChange={(e) => set("lyrics", e.target.value)} />
        </label>
      )}
      <label className="field">
        <span className="field-label">Style of Music</span>
        <textarea rows={6} maxLength={pack.limits.styleChars} value={d.style} onChange={(e) => set("style", e.target.value)} />
        <span className="field-hint">Describe the sound. Suno doesn't allow artist names, so we take them out.</span>
      </label>
      <label className="field">
        <span className="field-label">Exclude styles</span>
        <input type="text" maxLength={pack.limits.excludeChars} value={d.exclude} onChange={(e) => set("exclude", e.target.value)} />
      </label>
      <label className="field">
        <span className="field-label">Song title</span>
        <input type="text" maxLength={pack.limits.titleChars} value={d.title} onChange={(e) => set("title", e.target.value)} />
      </label>
      <label className="field suno-range">
        <span className="field-label">
          Weirdness <strong>{d.weirdness} %</strong>
        </span>
        <input type="range" min={0} max={100} step={5} value={d.weirdness} onChange={(e) => set("weirdness", Number(e.target.value))} />
      </label>
      <label className="field suno-range">
        <span className="field-label">
          Style Influence <strong>{d.styleInfluence} %</strong>
        </span>
        <input type="range" min={0} max={100} step={5} value={d.styleInfluence} onChange={(e) => set("styleInfluence", Number(e.target.value))} />
      </label>
      <label className="suno-check">
        <input type="checkbox" checked={d.maxMode} onChange={(e) => set("maxMode", e.target.checked)} /> Max Mode
      </label>
      <div className="suno-acts">
        <button type="button" className="btn btn-quiet" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </form>
  );
}

/** The song once it's in: play it, its length against the video's, and for a short song loop or shorten. */
function SunoSong({ p, s, onFit, busy = false }: { p: Project; s: SunoState; onFit?: (fit: "loop" | "shorten") => void; busy?: boolean }) {
  const playing = useStore((st) => st.playing === `suno-${p.id}`);
  const songUrl = s.assetId && p.music?.assetId === s.assetId ? p.music.url : null;
  return (
    <div className="suno-got">
      <div className="suno-song">
        <button type="button" className={`icon-btn${playing ? " is-on" : ""}`} disabled={!songUrl} aria-label={playing ? "Pause your song" : "Play your song"} onClick={() => toggleAudio(`suno-${p.id}`, songUrl)}>
          <Icon name={playing ? "pause" : "play"} size={18} />
        </button>
        <span className="suno-song-text">
          <strong>{s.songName ?? "Your Suno song"}</strong>
          <span>{s.songS ? `${time(s.songS)} · the video is ${time(s.videoS)}` : "It's the video's music now"}</span>
        </span>
      </div>
      {s.needsFit && onFit && (
        <div className="suno-fit" role="group" aria-label="The song is shorter than the video">
          <button data-action="suno.loop" type="button" className="btn btn-primary" disabled={busy} onClick={() => onFit("loop")}>
            Loop the song
          </button>
          <button data-action="suno.shorten" type="button" className="btn btn-quiet" disabled={busy} onClick={() => onFit("shorten")}>
            Shorten the video
          </button>
        </div>
      )}
    </div>
  );
}

/** The licensing note and the terms checkbox (first upload only) above the drop zone, which is pinned on a phone. */
function SunoUpload({ p, s, onSuno }: { p: Project; s: SunoState; onSuno: (s: SunoState) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [sent, setSent] = useState<number | null>(null);
  const [terms, setTerms] = useState<boolean | null>(null);
  const [ticked, setTicked] = useState(false);
  const [over, setOver] = useState(false);
  // the one-time terms checkbox: asked up front (GET /api/me/storage), and again if the upload answers 428
  useEffect(() => {
    let live = true;
    void api
      .myStorage()
      .then((x) => live && setTerms(x.termsAccepted))
      .catch(() => live && setTerms(false));
    return () => {
      live = false;
    };
  }, []);
  const needTerms = async () => terms === false;

  const send = async (file: File) => {
    const bad = songProblem(file);
    if (bad) return toast(bad, "bad");
    if ((await needTerms()) && !ticked) return toast("Tick the box first: it says you can use this song.", "bad");
    setSent(0);
    try {
      await api.uploadFile(file, { kind: "music", role: "music", origin: "suno", scope: { projectId: p.id }, ...(ticked ? { termsAccepted: true } : {}) }, setSent);
      setTerms(true);
      const next = await api.sunoPack(p.id);
      if (next) onSuno(next);
      director.context(`The user uploaded their Suno song "${file.name}" for "${p.title}" (${p.id}). It's the video's music now.`);
      toast("Your Suno song is in.", "good");
    } catch (e) {
      if (e instanceof ApiError && e.code === "TERMS_NEEDED") {
        setTerms(false);
        toast("Tick the box first: it says you can use this song.", "bad");
      } else toast(`Couldn't upload the song. ${(e as Error).message}`, "bad");
    } finally {
      setSent(null);
      if (input.current) input.current.value = "";
    }
  };

  const drop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    const f = e.dataTransfer.files[0];
    if (f) void send(f);
  };

  const prog = progressText(sent);
  return (
    <>
      <Notice tone="info" code="SUNO_LICENSE" text={SUNO_LICENSE_NOTE} />
      {terms === false && (
        <label className="suno-check suno-terms">
          <input type="checkbox" checked={ticked} onChange={(e) => setTicked(e.target.checked)} />
          <span>
            {UPLOAD_TERMS_CONSENT.label}{" "}
            <a href={UPLOAD_TERMS_CONSENT.link.href} target="_blank" rel="noopener noreferrer">
              {UPLOAD_TERMS_CONSENT.link.text}
            </a>
          </span>
        </label>
      )}
      <div
        className={`suno-drop${over ? " is-over" : ""}${prog ? " is-sending" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={drop}
      >
        {prog ? (
          <div className="suno-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round((sent ?? 0) * 100)} aria-label="Uploading your song">
            <span className="suno-bar" style={{ width: `${Math.max(4, Math.round((sent ?? 0) * 100))}%` }} />
            <span className="suno-prog-text">{prog}</span>
          </div>
        ) : (
          <button
            data-action="suno.upload"
            type="button"
            className={`btn ${s.status === "received" ? "btn-quiet" : "btn-primary"} suno-full`}
            onClick={async () => {
              if ((await needTerms()) && !ticked) return toast("Tick the box first: it says you can use this song.", "bad");
              input.current?.click();
            }}
          >
            <Icon name="music" size={16} /> {uploadLabel(s)}
          </button>
        )}
        <span className="suno-drop-hint">MP3 or WAV, up to 100 MB. Or drop it here.</span>
        <input ref={input} type="file" accept={SONG_ACCEPT} hidden onChange={(e) => e.target.files?.[0] && void send(e.target.files[0])} />
      </div>
    </>
  );
}
